// src/pos-app/ClockPanel.js
//
// Staff clock in and out at the till, without anyone signing out (client
// request 2026-08-26: "add a clock-in tab to the cashier sales screen and KDS
// so employees can come in quickly, clock in and go about their day").
//
// The backend and the flow already existed — POST /shifts/clock-in|clock-out
// and the four-stage modal in pages/pos/FunctionsPage.js:822-990. This is that
// flow rebuilt for the native shell: same service calls, same state machine,
// same station-pick step when the shift has no station assigned, restyled with
// the CSS-var tokens the rest of pos-app uses instead of FunctionsPage's
// inline C.* palette.
//
// THE property that matters: clocking in is NOT a login. shiftService.clockIn
// posts a PIN and gets back a shift — it never touches auth state, and this
// component must never start. A member of staff tapping Clock In is not taking
// over the cash drawer from whoever is running the till, and the operator
// session must survive untouched. Pinned by a localStorage assertion in the
// tests, same guard the manager gate carries.
import React, { useCallback, useEffect, useState } from 'react';
import { shiftService } from '../services/api';
import { useLanguage } from '../context/LanguageContext';

const MIN_PIN = 4;
const MAX_PIN = 6;
const STATIONS = ['Kitchen', 'Bar', 'Cashier', 'FOH'];

// Matches FunctionsPage's auto-dismiss so the panel returns to its resting
// state for the next person in the queue rather than holding one result.
const RESULT_DISMISS_MS = 4000;

const CSS = `
.clk-root{flex:1;min-height:0;display:flex;align-items:center;justify-content:center;padding:20px;
  box-sizing:border-box;font-family:'Plus Jakarta Sans',sans-serif;}
.clk-card{width:360px;max-width:100%;box-sizing:border-box;border-radius:20px;padding:26px 24px 22px;
  background:var(--c-surface,#fff);border:1px solid var(--c-border,#e2e8f0);
  box-shadow:0 10px 30px rgba(2,8,23,.10);}
.clk-title{margin:0 0 4px;font-size:17px;font-weight:800;text-align:center;color:var(--c-ink,#212529);}
.clk-sub{margin:0 0 18px;font-size:12.5px;font-weight:600;text-align:center;color:var(--c-muted,#64748b);}
.clk-actions{display:flex;gap:12px;}
.clk-action{flex:1;min-height:76px;border-radius:14px;border:none;cursor:pointer;font-family:inherit;
  font-size:14px;font-weight:800;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px;}
.clk-in{background:var(--c-green,#198754);color:#fff;}
.clk-out{background:var(--c-faint,#f1f5f9);color:var(--c-ink,#212529);border:1px solid var(--c-border,#e2e8f0);}
.clk-dots{display:flex;direction:ltr;justify-content:center;gap:13px;margin:16px 0;height:15px;align-items:center;}
.clk-dot{width:13px;height:13px;border-radius:50%;border:2px solid var(--c-mutedLight,#94a3b8);
  background:transparent;box-sizing:border-box;}
.clk-dot.fill{background:var(--c-green,#198754);border-color:var(--c-green,#198754);}
.clk-keypad{display:grid;direction:ltr;grid-template-columns:repeat(3,1fr);gap:10px;}
.clk-key{min-height:54px;border-radius:13px;cursor:pointer;touch-action:manipulation;
  background:var(--c-surface,#fff);color:var(--c-ink,#212529);border:1px solid var(--c-border,#e2e8f0);
  font-size:20px;font-weight:700;font-family:inherit;display:flex;align-items:center;justify-content:center;}
.clk-key:disabled{opacity:.5;cursor:default;}
.clk-submit{width:100%;min-height:50px;margin-top:14px;border:none;border-radius:13px;cursor:pointer;
  background:var(--c-green,#198754);color:#fff;font-size:15px;font-weight:800;font-family:inherit;}
.clk-submit:disabled{opacity:.45;cursor:default;}
.clk-back{width:100%;margin-top:8px;padding:10px;border:none;background:transparent;cursor:pointer;
  color:var(--c-muted,#64748b);font-size:12.5px;font-weight:700;font-family:inherit;text-decoration:underline;}
.clk-result{text-align:center;}
.clk-name{font-size:19px;font-weight:800;color:var(--c-ink,#212529);margin-bottom:4px;}
.clk-detail{font-size:13px;font-weight:600;color:var(--c-muted,#64748b);}
.clk-err{margin:12px 0 0;padding:10px 12px;border-radius:11px;text-align:center;font-size:12.5px;font-weight:700;
  color:var(--c-red,#dc2626);background:var(--c-redLight,#fef2f2);}
.clk-stations{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-top:14px;}
.clk-station{min-height:54px;border-radius:13px;cursor:pointer;font-family:inherit;font-size:13.5px;font-weight:800;
  background:var(--c-faint,#f1f5f9);color:var(--c-ink,#212529);border:1px solid var(--c-border,#e2e8f0);}
`;

export default function ClockPanel() {
    const { t } = useLanguage();

    const [mode, setMode] = useState('choose');   // choose | pin | result | pick_station | error
    const [action, setAction] = useState(null);   // 'in' | 'out'
    const [pin, setPin] = useState('');
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    const reset = useCallback(() => {
        setMode('choose');
        setAction(null);
        setPin('');
        setResult(null);
        setError('');
        setBusy(false);
    }, []);

    // Return to the resting state so the next person walks up to a fresh
    // panel rather than someone else's clock-in confirmation.
    useEffect(() => {
        if (mode !== 'result') return undefined;
        const id = setTimeout(reset, RESULT_DISMISS_MS);
        return () => clearTimeout(id);
    }, [mode, reset]);

    const submit = async () => {
        if (pin.length < MIN_PIN || busy) return;
        setBusy(true);
        setError('');
        try {
            const data = action === 'in'
                ? await shiftService.clockIn(pin)
                : await shiftService.clockOut(pin);
            setResult(data);
            setPin('');
            // A shift with no station assigned needs one before the KDS can
            // route to it — same branch FunctionsPage takes.
            if (action === 'in' && data?.station === 'Unassigned') setMode('pick_station');
            else setMode('result');
        } catch (err) {
            setError(err?.response?.data?.detail || t('clock_failed', {}, 'Could not clock in or out'));
            setMode('error');
            setPin('');
        } finally {
            setBusy(false);
        }
    };

    const pickStation = async (station) => {
        if (!result?.shift_id) return;
        try {
            await shiftService.patchStation(result.shift_id, station);
            setResult({ ...result, station });
        } catch (err) {
            console.error('Failed to set station:', err);
        }
        setMode('result');
    };

    const onPress = useCallback((fn) => (e) => {
        if (e && typeof e.button === 'number' && e.button !== 0) return;
        fn();
    }, []);

    const pressDigit = (d) => setPin((prev) => (prev.length >= MAX_PIN ? prev : prev + d));
    const pressBackspace = () => setPin((prev) => prev.slice(0, -1));

    return (
        <div className="clk-root">
            <style>{CSS}</style>
            <div className="clk-card">
                <h3 className="clk-title">{t('clock_in_out')}</h3>

                {mode === 'choose' && (<>
                    <p className="clk-sub">{t('clock_choose_action', {}, 'Choose an action to continue.')}</p>
                    <div className="clk-actions">
                        <button
                            type="button" className="clk-action clk-in" data-testid="clock-in"
                            onClick={() => { setAction('in'); setMode('pin'); }}
                        >{t('clock_in')}</button>
                        <button
                            type="button" className="clk-action clk-out" data-testid="clock-out"
                            onClick={() => { setAction('out'); setMode('pin'); }}
                        >{t('clock_out')}</button>
                    </div>
                </>)}

                {mode === 'pin' && (<>
                    <p className="clk-sub">
                        {action === 'in' ? t('clock_in') : t('clock_out')}
                    </p>
                    <div className="clk-dots" aria-hidden="true">
                        {Array.from({ length: MAX_PIN }, (_, i) => (
                            <span key={i} className={`clk-dot${i < pin.length ? ' fill' : ''}`} />
                        ))}
                    </div>
                    <div className="clk-keypad">
                        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
                            <button key={d} type="button" className="clk-key" disabled={busy}
                                onPointerDown={onPress(() => pressDigit(d))}>{d}</button>
                        ))}
                        <button type="button" className="clk-key" disabled={busy}
                            onPointerDown={onPress(pressBackspace)} aria-label={t('delete')}>&larr;</button>
                        <button type="button" className="clk-key" disabled={busy}
                            onPointerDown={onPress(() => pressDigit('0'))}>0</button>
                        <button type="button" className="clk-key" disabled={busy}
                            onPointerDown={onPress(reset)} aria-label={t('cancel')}>&#10005;</button>
                    </div>
                    <button
                        type="button" className="clk-submit"
                        disabled={pin.length < MIN_PIN || busy}
                        onClick={submit}
                    >{busy ? '…' : t('submit')}</button>
                </>)}

                {mode === 'pick_station' && (
                    <div data-testid="station-pick">
                        <p className="clk-sub">{t('clock_pick_station', {}, 'Where are you working today?')}</p>
                        <div className="clk-stations">
                            {STATIONS.map((station) => (
                                <button
                                    key={station} type="button" className="clk-station"
                                    data-testid={`station-${station}`}
                                    onClick={() => pickStation(station)}
                                >{t(station.toLowerCase(), {}, station)}</button>
                            ))}
                        </div>
                    </div>
                )}

                {mode === 'result' && (
                    <div className="clk-result">
                        <div className="clk-name">{result?.employee_name}</div>
                        <div className="clk-detail">
                            {action === 'in'
                                ? t('clocked_in_at_station', { station: result?.station || '' },
                                    `Clocked in — ${result?.station || ''}`)
                                : t('clocked_out_hours', { hours: result?.hours_worked ?? '' },
                                    `Clocked out — ${result?.hours_worked ?? ''}h`)}
                        </div>
                        <button type="button" className="clk-back" onClick={reset}>{t('done', {}, 'Done')}</button>
                    </div>
                )}

                {mode === 'error' && (<>
                    <p className="clk-err" role="alert">{error}</p>
                    <button type="button" className="clk-submit" onClick={() => { setMode('pin'); setError(''); }}>
                        {t('try_again', {}, 'Try again')}
                    </button>
                    <button type="button" className="clk-back" onClick={reset}>{t('cancel')}</button>
                </>)}
            </div>
        </div>
    );
}
